import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Dashboard } from '../components/dashboard';
import { RequireAuth } from '../components/require-auth';
import { RouteContainer } from '../components/route-container';
import { usePermissions } from '../hooks/use-permissions';
import { AWS_GROUP } from '../types';
import { History } from './history';
import { Home } from './home';
import { Leads } from './leads';
import { LeadsMap } from './leads-map';
import { MonthlyStatements } from './monthly-statements';
import { NotFound } from './not-found';
import { SignIn } from './sign-in';
import { Subscriptions } from './subscriptions';
import { Team } from './team';
import { WeeklyInvoices } from './weekly-invoices';

const AppRoutes = {
  Admin: React.lazy(() => import('./admin')),
};

/** While the admin chunk downloads: the shell's loading bar, empty page */
function AdminLoading() {
  return (
    <RouteContainer routeTitle="Admin" loading>
      <div />
    </RouteContainer>
  );
}

export function Root() {
  // Hooks
  const { checkPermissions } = usePermissions();

  // Query - Check for Maintenance
  const pathMaint = process.env.REACT_APP_MAINT_URL;
  const queryMaint = useQuery({
    enabled: pathMaint !== undefined,
    queryKey: [pathMaint],
    queryFn: async () => {
      if (pathMaint) {
        const body = { data: { origin: window.origin } };
        let response: Response;
        try {
          response = await fetch(pathMaint, {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
        } catch (error) {
          // The maintenance check is unreachable (offline, blocked): fail open
          // rather than logging "Failed to fetch" on every page load
          return null;
        }
        const data: {
          data: {
            maintenanceActive: boolean;
            maintenanceBegin: string;
            maintenanceEnd: string;
          };
        } = await response.json();

        return data.data;
      }

      return null;
    },
  });

  const isAdmin = checkPermissions([
    AWS_GROUP.ARC_GlobalAdmin,
    AWS_GROUP.LDS_GlobalAdmin,
    AWS_GROUP.LDS_KeyingMgr,
  ]);

  // Agents cannot access the app during Maintenance (RequireAuth shows the
  // maintenance screen). Admins can, with a banner under the App Bar
  const adminMaintenanceEnd =
    isAdmin && queryMaint.data && queryMaint.data.maintenanceActive
      ? queryMaint.data.maintenanceEnd || ''
      : null;

  return (
    <Routes>
      <Route path="sign-in" element={<SignIn />} />

      <Route
        path="/"
        element={
          <RequireAuth isAdmin={isAdmin} maintData={queryMaint.data}>
            <Dashboard maintenanceEnd={adminMaintenanceEnd} />
          </RequireAuth>
        }
      >
        <Route path="home" element={<Home />} />
        <Route
          path="admin/*"
          element={
            isAdmin ? (
              <React.Suspense fallback={<AdminLoading />}>
                <AppRoutes.Admin />
              </React.Suspense>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route path="map" element={<LeadsMap />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="team" element={<Team />} />
        <Route path="leads" element={<Leads />} />
        <Route path="history" element={<History />} />
        <Route path="invoices" element={<WeeklyInvoices />} />
        <Route path="monthly-statements" element={<MonthlyStatements />} />

        <Route path="/" element={<Navigate to="home" />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
